import { CreditCardIcon } from '@heroicons/react/outline';
import * as React from 'react';
import { Translation } from 'react-i18next';

import { NewFreeReminderTemplate } from '@/components/Notifications/NotificationTemplates/NewFreeReminderTemplate';
import { NoBillingReminderTemplate } from '@/components/Notifications/NotificationTemplates/NoBillingReminderTemplate';
import {
  NOTIFICATION_CHECKOUT_REMINDER_ID,
  NOTIFICATION_NEW_FREE_REMINDER_ID,
  NOTIFICATION_NO_BILLING_REMINDER_ID,
} from '@/config';
import { BillingDetails } from '@/features/billing';
import { useNotificationStore } from '@/stores/notifications';
import { Currency, IntervalBillingMode, Me, ProductTypes } from '@/types';
import { isNewFreeUserFromMe } from '@/utils/helpers';

import { PaymentReminderTemplate } from '../NotificationTemplates/PaymentReminderTemplate';

export const createNoBillingReminderNotification = () => {
  useNotificationStore.getState().addNotification({
    id: NOTIFICATION_NO_BILLING_REMINDER_ID,
    type: 'cardReminder',
    title: (
      <b>
        <Translation>{(t) => t('PrivateLayout.reminderTitle')}</Translation>
      </b>
    ),
    closeable: false,
    message: <NoBillingReminderTemplate />,
  });
};

export const showNotificationNoBilling = (billingDetails: BillingDetails | null, me: Me) => {
  if (!billingDetails?.alreadySaved && isNewFreeUserFromMe(me)) {
    createNoBillingReminderNotification();
  } else {
    dismissNoBillingReminderNotification();
  }
};

export const createNewFreeNotification = () => {
  useNotificationStore.getState().addNotification({
    id: NOTIFICATION_NEW_FREE_REMINDER_ID,
    type: 'cardReminder',
    title: (
      <b>
        <Translation>{(t) => t('PrivateLayout.reminderTitle')}</Translation>
      </b>
    ),
    closeable: false,
    message: <NewFreeReminderTemplate />,
  });
};

export const showNotificationNewFree = (billingDetails: BillingDetails | null, me: Me) => {
  if (billingDetails?.alreadySaved && isNewFreeUserFromMe(me)) {
    createNewFreeNotification();
  } else {
    dismissNewFreeNotification();
  }
};

export const dismissNoBillingReminderNotification = () =>
  useNotificationStore.getState().dismissNotification(NOTIFICATION_NO_BILLING_REMINDER_ID);

export const dismissNewFreeNotification = () =>
  useNotificationStore.getState().dismissNotification(NOTIFICATION_NEW_FREE_REMINDER_ID);

export const dismissPaymentReminderNotification = (mdirectorId: string) =>
  useNotificationStore
    .getState()
    .dismissNotification(`${NOTIFICATION_CHECKOUT_REMINDER_ID}${mdirectorId}`);

export const createPaymentReminderNotification = (
  product: ProductTypes,
  currency: Currency,
  intervalBillingMode: IntervalBillingMode | null,
  onActionButton: (
    product: ProductTypes,
    currency: Currency,
    intervalBillingMode: IntervalBillingMode | null
  ) => void,
  onCancelButton: (product: ProductTypes) => void
) => {
  useNotificationStore.getState().addNotification({
    id: `${NOTIFICATION_CHECKOUT_REMINDER_ID}${product?.mdirectorId}`,
    type: 'warning',
    title: (
      <b>
        <Translation>{(t) => t('Forms.Checkout.reminder.description')}</Translation>
      </b>
    ),
    message: (
      <PaymentReminderTemplate
        onActionButton={onActionButton}
        onCancelButton={() => onCancelButton(product)}
        product={product}
        currency={currency}
        intervalBillingMode={intervalBillingMode}
      />
    ),
    customIcon: <CreditCardIcon className="h-6 w-6" aria-hidden="true" />,
    closeable: false,
    accent: 'left',
  });
};
